/**
 * LiveActivity — visitors-on-storefront dashboard.
 *
 * 4 stat tiles, Active Visitors panel (left), Global Feed (right).
 * Realtime via the `/storefront-chatbot` socket namespace; falls back to
 * polling when socket disconnects. Empty state nudges merchants to wait.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { io as socketIo } from 'socket.io-client';
import {
  Activity, Eye, MousePointerClick, Users, Globe2, Smartphone, Monitor, DollarSign,
  ShoppingCart, ScrollText, EyeOff, MoveRight, Search, Tag, FileQuestion, Pause, Play,
} from 'lucide-react';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import axiosInstance from '../../axiosConfig';
import './LiveActivity.css';

const VISITORS_POLL_MS = 6000;
const FEED_POLL_MS = 6000;
const STATS_POLL_MS = 15000;

function getSocketUrl() {
  if (process.env.REACT_APP_BACKEND_URL) return process.env.REACT_APP_BACKEND_URL;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:4000';
  }
  return 'https://revstar-main-platform-production.up.railway.app';
}
function getMerchantToken() {
  return localStorage.getItem('token') || sessionStorage.getItem('token') || null;
}

// --- Helpers --------------------------------------------------------------

function initials(visitor) {
  const src = visitor?.name || visitor?.displayName || 'G';
  return src.replace(/^Guest\s*#?/i, 'G').trim().split(/\s+/).slice(0, 2)
    .map((p) => (p[0] || '').toUpperCase()).join('') || 'G';
}

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 5) return 'now';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

const EVENT_ICON = {
  page_view:        Eye,
  navigated:        MoveRight,
  click:            MousePointerClick,
  scroll:           ScrollText,
  tab_hidden:       EyeOff,
  tab_visible:      Eye,
  viewing_product:  Tag,
  viewing_category: Tag,
  add_to_cart:      ShoppingCart,
  begin_checkout:   ShoppingCart,
  hover_significant: MousePointerClick,
  form_focus:       MousePointerClick,
  search:           Search,
};

const EVENT_LABEL = {
  page_view:        'Viewed page',
  navigated:        'Navigated to',
  click:            'Clicked',
  scroll:           'Scrolled',
  tab_hidden:       'Tab hidden',
  tab_visible:      'Tab visible',
  viewing_product:  'Viewing product',
  viewing_category: 'Viewing category',
  add_to_cart:      'Added to cart',
  begin_checkout:   'Started checkout',
  hover_significant: 'Hovered on',
  form_focus:       'Focused field',
  search:           'Searched',
};

function describeEvent(ev) {
  const label = EVENT_LABEL[ev.eventType] || ev.eventType.replace(/_/g, ' ');
  const detail = ev.targetText || ev.pageTitle || ev.target || ev.pagePath;
  return { label, detail };
}

// --- Stat tile ------------------------------------------------------------

const StatTile = ({ label, value, icon: Icon }) => (
  <div className="la-stat">
    <div className="la-stat__top">
      <span className="la-stat__label">{label}</span>
      <span className="la-stat__icon" aria-hidden><Icon size={16} /></span>
    </div>
    <div className="la-stat__value">{value?.toLocaleString?.() ?? value ?? '—'}</div>
  </div>
);

// --- Active Visitors panel ------------------------------------------------

const VisitorCard = ({ visitor, onTakeOver }) => {
  const Device = visitor.device === 'mobile' || visitor.device === 'tablet' ? Smartphone : Monitor;
  const [takingOver, setTakingOver] = useState(false);

  const handleClick = async () => {
    if (takingOver) return;
    setTakingOver(true);
    try {
      await onTakeOver(visitor);
    } finally {
      setTakingOver(false);
    }
  };

  return (
    <article className="la-visitor">
      <div className="la-visitor__head">
        <span className="la-visitor__avatar" aria-hidden>{initials(visitor)}</span>
        <div className="la-visitor__body">
          <p className="la-visitor__name">{visitor.displayName || visitor.name || 'Guest'}</p>
          <p className="la-visitor__meta">
            {visitor.country && (
              <>
                <Globe2 size={11} />
                <span>{[visitor.city, visitor.country].filter(Boolean).join(', ')}</span>
                <span className="la-visitor__meta-dot" />
              </>
            )}
            <Device size={11} />
            <span>{[visitor.os, visitor.browser].filter(Boolean).join(' · ') || visitor.device || 'unknown'}</span>
          </p>
        </div>
      </div>

      {Array.isArray(visitor.recentEvents) && visitor.recentEvents.length > 0 && (
        <ul className="la-visitor__events">
          {visitor.recentEvents.slice(0, 5).map((ev) => {
            const Icon = EVENT_ICON[ev.eventType] || FileQuestion;
            const { label, detail } = describeEvent(ev);
            return (
              <li key={ev.id} className="la-visitor__event">
                <Icon className="la-visitor__event-icon" size={12} />
                <span className="la-visitor__event-text">
                  <strong>{label}</strong>
                  {detail ? ` — ${detail}` : ''}
                </span>
                <span className="la-visitor__event-time">{relTime(ev.createdAt)}</span>
              </li>
            );
          })}
        </ul>
      )}

      <button
        type="button"
        className="la-visitor__cta"
        onClick={handleClick}
        disabled={takingOver}
      >
        {visitor.openConversationId ? 'Continue chat' : (takingOver ? 'Opening…' : 'Open chat')}
      </button>
    </article>
  );
};

// --- Global feed ---------------------------------------------------------

const FeedRow = ({ event: ev }) => {
  const Icon = EVENT_ICON[ev.eventType] || FileQuestion;
  const { label, detail } = describeEvent(ev);
  const who = ev.visitor?.displayName || ev.visitor?.name || 'Guest';
  return (
    <div className="la-feed-row">
      <span className="la-feed-row__icon" aria-hidden><Icon size={13} /></span>
      <div className="la-feed-row__body">
        <p className="la-feed-row__line">
          <strong>{who}</strong>
          {ev.visitor?.country ? ` · ${ev.visitor.country}` : ''}
          {' — '}
          {label}
          {detail ? `: ${detail}` : ''}
        </p>
        {ev.pagePath && <p className="la-feed-row__sub">{ev.pagePath}</p>}
      </div>
      <span className="la-feed-row__time">{relTime(ev.createdAt)}</span>
    </div>
  );
};

// --- Page ----------------------------------------------------------------

const LiveActivity = () => {
  const navigate = useNavigate();

  const [visitors, setVisitors] = useState([]);
  const [feed, setFeed] = useState([]);
  const [stats, setStats] = useState({ onlineNow: 0, eventsTracked24h: 0, pageViews24h: 0, clicks24h: 0 });
  const [attribution, setAttribution] = useState({ ordersAttributed: 0, revenueAttributed: 0, conversationsConverted: 0 });
  const [paused, setPaused] = useState(false);
  const [, forceTick] = useState(0); // re-render every 5s for live "Xs ago"
  const [initialLoading, setInitialLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const socketRef = useRef(null);
  const [socketConnected, setSocketConnected] = useState(false);

  // Tick the clock so relTime() updates without a full re-fetch
  useEffect(() => {
    const t = setInterval(() => forceTick((n) => n + 1), 5000);
    return () => clearInterval(t);
  }, []);

  // --- Loaders ------------------------------------------------------------

  const loadVisitors = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/merchant/chatbot/live-activity/visitors');
      setVisitors(res?.data?.visitors || []);
      setFetchError(null);
    } catch (err) {
      console.error('[LiveActivity] visitors load:', err?.response?.data?.error || err?.message);
      setFetchError(err?.response?.data?.error || 'Could not reach live activity. Check your connection.');
    }
  }, []);

  const loadFeed = useCallback(async () => {
    if (paused) return;
    try {
      const res = await axiosInstance.get('/merchant/chatbot/live-activity/feed', { params: { limit: 80 } });
      setFeed(res?.data?.events || []);
      setFetchError(null);
    } catch (err) {
      console.error('[LiveActivity] feed load:', err?.response?.data?.error || err?.message);
    }
  }, [paused]);

  const loadStats = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/merchant/chatbot/live-activity/stats');
      setStats(res?.data?.stats || stats);
    } catch (err) {
      console.error('[LiveActivity] stats load:', err?.response?.data?.error || err?.message);
    }
  }, [stats]);

  const loadAttribution = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/merchant/chatbot/attribution', { params: { range: '30d' } });
      setAttribution({
        ordersAttributed: res?.data?.ordersAttributed || 0,
        revenueAttributed: res?.data?.revenueAttributed || 0,
        conversationsConverted: res?.data?.conversationsConverted || 0,
      });
    } catch (err) {
      console.error('[LiveActivity] attribution load:', err?.response?.data?.error || err?.message);
    }
  }, []);

  useEffect(() => {
    Promise.all([loadVisitors(), loadFeed(), loadStats(), loadAttribution()]).finally(() => setInitialLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Polling fallback ---------------------------------------------------

  useEffect(() => {
    const interval = socketConnected ? 30000 : VISITORS_POLL_MS;
    const t = setInterval(loadVisitors, interval);
    return () => clearInterval(t);
  }, [loadVisitors, socketConnected]);

  useEffect(() => {
    const interval = socketConnected ? 30000 : FEED_POLL_MS;
    const t = setInterval(loadFeed, interval);
    return () => clearInterval(t);
  }, [loadFeed, socketConnected]);

  useEffect(() => {
    const t = setInterval(loadStats, STATS_POLL_MS);
    return () => clearInterval(t);
  }, [loadStats]);

  // --- Socket -------------------------------------------------------------

  useEffect(() => {
    // Merchant auth rides on the HTTP-only `revstar_merchant_token` cookie
    // via withCredentials — there's no localStorage token to read.
    const sock = socketIo(`${getSocketUrl()}/storefront-chatbot`, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });
    socketRef.current = sock;
    sock.on('connect', () => setSocketConnected(true));
    sock.on('disconnect', () => setSocketConnected(false));
    sock.on('connect_error', () => setSocketConnected(false));

    sock.on('live_activity:event_batch', (payload) => {
      if (paused) return;
      const incoming = (payload?.events || []).map((e, i) => ({
        id: `live-${payload.visitorId}-${Date.now()}-${i}`,
        eventType: e.eventType,
        pagePath: e.pagePath,
        pageTitle: e.pageTitle,
        target: e.target,
        targetText: e.targetText,
        productId: e.productId,
        createdAt: e.createdAt || new Date().toISOString(),
        visitor: { displayName: payload.visitorDisplayName, name: null, country: null },
      }));
      if (incoming.length > 0) {
        setFeed((prev) => [...incoming, ...prev].slice(0, 160));
      }
    });
    sock.on('live_activity:visitor_active', () => {
      // Refresh visitors so the new card appears with full geo/device data
      loadVisitors();
      loadStats();
    });
    sock.on('live_activity:visitor_idle', ({ visitorId }) => {
      setVisitors((prev) => prev.filter((v) => v.id !== visitorId));
      loadStats();
    });

    return () => {
      sock.removeAllListeners();
      sock.disconnect();
      socketRef.current = null;
    };
  }, [paused, loadVisitors, loadStats]);

  // --- Actions ------------------------------------------------------------

  const handleTakeOver = useCallback(async (visitor) => {
    try {
      if (visitor.openConversationId) {
        navigate(`/storefront-chat/${visitor.openConversationId}`);
        return;
      }
      const res = await axiosInstance.post('/merchant/chatbot/live-activity/take-over', {
        visitorId: visitor.id,
      });
      const convId = res?.data?.conversation?.id;
      if (convId) navigate(`/storefront-chat/${convId}`);
    } catch (err) {
      console.error('[LiveActivity] take-over:', err?.message);
    }
  }, [navigate]);

  // --- Render -------------------------------------------------------------

  const visitorCount = visitors.length;

  return (
    <Shell>
      <FeatureGate featureId="sales.storefront_chatbot" upgradeMessage="Upgrade to PRO or PREMIUM to see live storefront activity.">
        <div className="la-page">
          <header className="la-header">
            <div>
              <h1 className="la-header__title">
                Live activity
                {visitorCount > 0 && (
                  <span className="la-pill">
                    <span className="la-pill__dot" aria-hidden />
                    {visitorCount} visitor{visitorCount === 1 ? '' : 's'} now
                  </span>
                )}
              </h1>
              <p className="la-header__sub">Watch what shoppers do in real time. Click a visitor to start a chat.</p>
            </div>
            <nav className="la-header__links" aria-label="Switch view">
              <Link to="/storefront-chat" className="la-header__link">Inbox</Link>
              <Link to="/storefront-chat/live" className="la-header__link is-active">Live</Link>
              <Link to="/storefront-chat/settings" className="la-header__link">Settings</Link>
            </nav>
          </header>

          {fetchError && (
            <div style={{
              padding: '10px 14px',
              background: 'rgba(185, 28, 28, 0.06)',
              border: '1px solid rgba(185, 28, 28, 0.20)',
              color: '#b91c1c',
              borderRadius: 10,
              fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
            }}>
              <span><strong style={{ fontWeight: 700 }}>Could not load live activity:</strong> {fetchError}</span>
              <button
                type="button"
                onClick={() => { setInitialLoading(true); Promise.all([loadVisitors(), loadFeed(), loadStats()]).finally(() => setInitialLoading(false)); }}
                style={{
                  padding: '5px 12px', fontSize: 12, fontWeight: 600,
                  background: '#b91c1c', color: '#fff', border: 'none', borderRadius: 6,
                  cursor: 'pointer', width: 'auto', maxWidth: 'max-content',
                }}
              >Retry</button>
            </div>
          )}

          <section className="la-stats" aria-label="Live activity stats">
            <StatTile label="Online now" value={stats.onlineNow} icon={Activity} />
            <StatTile label="Events (24h)" value={stats.eventsTracked24h} icon={Eye} />
            <StatTile label="Page views (24h)" value={stats.pageViews24h} icon={Eye} />
            <StatTile label="Clicks (24h)" value={stats.clicks24h} icon={MousePointerClick} />
          </section>

          {/* Phase 9.6: chat-driven revenue rollup. Hidden until something
              actually converted so we don't show "₦0" on day 1. */}
          {attribution.ordersAttributed > 0 && (
            <section className="la-stats la-stats--attribution" aria-label="Chat-driven sales (last 30 days)">
              <div className="la-stat la-stat--accent">
                <div className="la-stat__top">
                  <span className="la-stat__label">Revenue from chats (30d)</span>
                  <span className="la-stat__icon" aria-hidden><DollarSign size={16} /></span>
                </div>
                <div className="la-stat__value">{attribution.revenueAttributed.toLocaleString()}</div>
              </div>
              <StatTile label="Orders attributed" value={attribution.ordersAttributed} icon={DollarSign} />
              <StatTile label="Conversations that converted" value={attribution.conversationsConverted} icon={Users} />
            </section>
          )}

          <div className="la-grid">
            <aside className="la-panel">
              <header className="la-panel__header">
                <h2 className="la-panel__title">Active visitors</h2>
                <span className="la-panel__count">{visitorCount}</span>
              </header>
              <div className="la-panel__body">
                {initialLoading ? (
                  <div className="la-empty">
                    <span className="la-empty__icon" aria-hidden><Activity size={22} /></span>
                    <p className="la-empty__title">Loading visitors…</p>
                  </div>
                ) : visitorCount === 0 ? (
                  <div className="la-empty">
                    <span className="la-empty__icon" aria-hidden><Users size={22} /></span>
                    <p className="la-empty__title">No one on your storefront right now</p>
                    <p className="la-empty__body">Once a shopper lands on a page, you'll see them here with their live activity. Make sure the chatbot is enabled in <Link to="/storefront-chat/settings" style={{ color: 'var(--la-brand)' }}>settings</Link> so the tracker can run.</p>
                  </div>
                ) : (
                  visitors.map((v) => (
                    <VisitorCard key={v.id} visitor={v} onTakeOver={handleTakeOver} />
                  ))
                )}
              </div>
            </aside>

            <section className="la-panel">
              <header className="la-panel__header">
                <h2 className="la-panel__title">Global feed</h2>
                <div className="la-feed-controls">
                  <button
                    type="button"
                    className={`la-feed-btn ${paused ? 'is-on' : ''}`}
                    onClick={() => setPaused((p) => !p)}
                    aria-pressed={paused}
                  >
                    {paused ? <><Play size={11} style={{ verticalAlign: 'middle' }} /> Resume</> : <><Pause size={11} style={{ verticalAlign: 'middle' }} /> Pause</>}
                  </button>
                </div>
              </header>
              <div className="la-panel__body">
                {initialLoading ? (
                  <div className="la-empty">
                    <span className="la-empty__icon" aria-hidden><Activity size={22} /></span>
                    <p className="la-empty__title">Loading feed…</p>
                  </div>
                ) : feed.length === 0 ? (
                  <div className="la-empty">
                    <span className="la-empty__icon" aria-hidden><Activity size={22} /></span>
                    <p className="la-empty__title">Quiet for now</p>
                    <p className="la-empty__body">Every page view, click, and scroll on your storefront streams in here. Events from the past 48h appear automatically once visitors browse your store.</p>
                  </div>
                ) : (
                  feed.map((ev) => <FeedRow key={ev.id} event={ev} />)
                )}
              </div>
            </section>
          </div>
        </div>
      </FeatureGate>
    </Shell>
  );
};

export default LiveActivity;
